import { Event, EventEmitter, EventListener, View, ViewData, EventHandler } from "silver";
import { Listbox } from "core/Controls/Listbox";
import { StylizedTabset } from "core/Controls/StylizedTabset";
import { debounce } from "shared/functions";

export class SearchShuttleDetailRow<D extends string|number> implements SearchShuttleRow<D> {
	public readonly element: HTMLElement = document.createElement("div");
	protected readonly titleNode: HTMLElement = document.createElement("div");
	protected readonly controlsNode: HTMLElement = document.createElement("div");
	protected readonly detailsNode: HTMLElement = document.createElement("div");

	constructor(
		public readonly record: SearchShuttleDbRecord<D>
	) {
		this.element.classList.add("detailRow");
		this.titleNode.classList.add("detailRow_title");
		this.controlsNode.classList.add("detailRow_controls");
		this.detailsNode.classList.add("detailRow_details");

		this.element.appendChild(this.titleNode);
		this.element.appendChild(this.detailsNode);
		this.element.appendChild(this.controlsNode);

		this.record.onActiveChange(() => {
			this.handleActiveChanged();
		});
		this.handleActiveChanged();
	}

	protected handleActiveChanged(): void {
		this.element.classList.toggle("-info", this.record.active && !this.record.defaultActive);
		this.element.classList.toggle("-warning", !this.record.active && this.record.defaultActive);
	}

	public setTitle(title: string, isLoading: boolean = false): void {
		this.element.classList.toggle("-loading", isLoading);
		this.titleNode.innerText = title;
	}

	public addControl(action: HTMLElement): void {
		this.controlsNode.appendChild(action);
	}

	public addDetail(detail: string, classList: string[] = []): void {
		const element = document.createElement("div");
		this.detailsNode.appendChild(element);
		element.classList.add("detailRow_detail", ...classList);
		element.innerText = detail;
	}
}


export interface SearchModule<Q, D extends string|number> {
	onSubmit: EventEmitter<Event>;
	onResponse: EventEmitter<SearchResponseEvent<Q, D>>;
	clear(): void;
	element: HTMLElement;
	value: Q;
}

interface SearchResponseEvent<Q, D extends string|number> extends Event {
	results: D[];
	sender: SearchModule<Q, D>;
}

export abstract class BaseSearchModule<Q, D extends string|number> implements SearchModule<Q, D> {
	public readonly onSubmit = EventListener.create<Event>("submit");
	public readonly onResponse = EventListener.create<SearchResponseEvent<Q, D>>("response");
	protected readonly label = document.createElement("label");
	public abstract value: Q;
	public abstract element: HTMLElement;
	private delayTimer: number = 0;
	private searchIndex: number = 0;
	protected constructor(protected fetcher: (value: Q) => Promise<D[]>) { }
	public clear(): void {
		this.searchIndex++;
	}
	protected fetchOnDelay(delay: number): void {
		clearTimeout(this.delayTimer);
		const searchIndex = ++this.searchIndex;
		this.delayTimer = setTimeout(async (): Promise<void> => {
			const value = this.value;
			if (!value) {
				return;
			}
			this.onSubmit.trigger({ });
			const results = await this.fetcher(value);
			if (searchIndex == this.searchIndex) {
				this.onResponse.trigger({ results, sender: this });
			}
		}, delay);
	}
}

export class TextInputSearchModule<D extends string|number> extends BaseSearchModule<string, D> {
	public readonly element: HTMLInputElement = document.createElement("input");
	constructor(placeholder: string, fetcher: (value: string) => Promise<D[]>) {
		super(fetcher);
		this.element.classList.add("input", "-search", "form_textInput");
		this.element.type = "text";
		this.element.placeholder = placeholder;
		this.element.addEventListener("keyup", () => {
			this.fetchOnDelay(300);
		});
	}
	public get value(): string {
		return this.element.value;
	}
	clear(): void {
		super.clear();
		this.element.value = "";
	}
}

function isOption(value: Element): value is HTMLOptionElement {
	return value.tagName == "option";
}

export type Option = { label: string, value: string };
export class SelectSearchModule<D extends string|number> extends BaseSearchModule<string, D> {
	public readonly element: HTMLSelectElement = document.createElement("select");
	constructor(options: Option[] = [], fetcher: (value: string) => Promise<D[]>) {
		super(fetcher);
		this.element.classList.add("select", "form_select");
		this.element.addEventListener("change", () => {
			this.fetchOnDelay(100);
		});
		this.setOptions(options);
	}
	public setOptions(options: Option[]): void {
		this.element.innerHTML = "";
		this.addOption({ label: "—", value: "" });
		options.forEach((option) => this.addOption(option));
	}
	public addOption(option: Option): void {
		const element = document.createElement("option");
		element.value = option.value;
		element.innerText = option.label;
		this.element.appendChild(element);
	}
	public removeOption(value: D): void {
		const strVal = String(value);
		Array.from(this.element.children).filter(isOption).forEach((option) => {
			if (option.value === strVal) {
				this.element.removeChild(option);
			}
		});
	}
	public get value(): string {
		return this.element.value;
	}
	clear(): void {
		super.clear();
		this.element.selectedIndex = 0;
	}
}

export interface SearchShuttleCandidateControl<D extends string|number, R extends SearchShuttleRow<D> = SearchShuttleRow<D>> {
	readonly onRowVisibilityChange: EventEmitter<Event>;
	readonly onRowsCreated: EventEmitter<{ rows: R[] }>;
	element: HTMLElement;
	setDatabase(database: SearchShuttleDb<D>): void;
	getVisibleItems(pageSize: number): R[];
	scrollIntoView(value: D): void;
	clear(): void;
	setHeight(height: number|null): void;
}

export class SearchModuleSearchShuttleCandidateControl<D extends string|number, R extends SearchShuttleRow<D> = SearchShuttleRow<D>> implements SearchShuttleCandidateControl<D, R> {

	public readonly onRowVisibilityChange = EventListener.create<Event>("rowVisibilityChange");
	public readonly onRowsCreated = EventListener.create<{ rows: R[] }>("rowsCreated");
	public readonly element = document.createElement("div");
	private readonly selectAllContainer = document.createElement("label");
	private readonly selectAllCheckbox = document.createElement("input");
	public readonly listbox: Listbox<D> = new Listbox<D>();
	private database?: SearchShuttleDb<D>;
	private controls: Record<string, R> = { };

	constructor(
		module: SearchModule<any, D>,
		protected rowFactory: (record: SearchShuttleDbRecord<D>) => R,
		searchingMessage: string,
		noResultsMessage: string,
		selectAllMessage: string,
		private sortDefaultToTop: boolean = true
	) {
		this.listbox.selectable = false;
		this.listbox.onScroll(() => this.onRowVisibilityChange.trigger({ }));
		this.listbox.onModified(() => {
			const database = this.database;
			if (database && this.sortDefaultToTop) {
				this.listbox.sort((a, b) => {
					const aActive = database.getEntry(a)?.defaultActive;
					const bActive = database.getEntry(b)?.defaultActive;
					if (aActive && !bActive) {
						return -1;
					}
					if (!aActive && bActive) {
						return 1;
					}
					return 0;
				});
			}
			this.onRowVisibilityChange.trigger({ });
		});

		this.element.classList.add("card", "g_noMargin", "flex_item", "-grow", "flex", "-column", "-moat", "-gutter");
		module.element.classList.add("flex_item");
		this.listbox.element.classList.add("flex_item", "-grow", "-flex", "-overflow");

		this.element.appendChild(module.element);
		this.element.appendChild(this.listbox.element);
		this.element.appendChild(this.selectAllContainer);

		this.selectAllContainer.classList.add("flex_item", "flex", "-row", "-centerLeft");
		this.selectAllContainer.appendChild(this.selectAllCheckbox);
		this.selectAllContainer.appendChild(document.createTextNode(selectAllMessage));
		this.selectAllCheckbox.setAttribute("type", "checkbox");
		this.selectAllCheckbox.classList.add("checkbox");
		this.selectAllCheckbox.addEventListener("change", () => {
			if (!this.database) {
				throw new Error("No database");
			}
			const ids = this.listbox.getAllOptions();
			for (let i = 0; i < ids.length; i++) {
				const entry = this.database.getEntry(ids[i].id);
				if (entry && !entry.defaultActive) {
					entry.active = this.selectAllCheckbox.checked;
				}
			}
		});

		module.onSubmit(() => {
			this.listbox.showMessage(searchingMessage);
		});
		module.onResponse(({ results }) => {
			if (!this.database) {
				throw new Error("No attached database");
			}
			this.listbox.clear();
			if (results.length === 0) {
				this.listbox.showMessage(noResultsMessage);
				return;
			}
			this.listbox.hideMessage();
			const missingControls = results.filter(id => !this.controls.hasOwnProperty(String(id)));
			const records = this.database.registerMany(missingControls.map(id => ({ value: id, active: false, defaultActive: false })));
			const rows: R[] = [];

			missingControls.forEach(id => {
				this.controls[String(id)] = this.rowFactory(records[String(id)]);
				rows.push(this.controls[String(id)]);
			});
			this.onRowsCreated.trigger({ rows });
			this.listbox.addMany(results.map(id => ({ id, node: this.controls[String(id)].element })));
		});
	}

	setDatabase(database: SearchShuttleDb<D>): void {
		this.database = database;
	}

	clear(): void {
		this.listbox.clear();
	}

	getVisibleItems(pageSize: number): R[] {
		return this.listbox.getVisibleOptions(pageSize).map(({ id }) => this.controls[String(id)]);
	}

	scrollIntoView(value: D): void {
		this.listbox.scrollIntoView(value);
	}

	setHeight(height: number|null): void {
		this.listbox.height = height ? (height - 40) : height;
	}
}

export class TabsetSearchShuttleCandidateControl<D extends string|number, R extends SearchShuttleRow<D> = SearchShuttleRow<D>> implements SearchShuttleCandidateControl<D, R> {

	public readonly onRowVisibilityChange = EventListener.create<Event>("rowVisibilityChange");
	public readonly onRowsCreated = EventListener.create<{ rows: R[] }>("rowCreated");
	public readonly element;
	private readonly tabset: StylizedTabset;
	private controls: Array<SearchShuttleCandidateControl<D, R>> = [ ];
	private database?: SearchShuttleDb<D>;

	constructor(rockerLeftTitle: string, rockerRightTitle: string) {
		this.tabset = StylizedTabset.make(rockerLeftTitle, rockerRightTitle);
		this.element = this.tabset.element;
		this.tabset.onTabActivate(() => {
			this.onRowVisibilityChange.trigger({ });
		});
	}

	public addControl(title: string, control: SearchShuttleCandidateControl<D, R>): void {
		const tab = StylizedTabset.makeTabFromContent(String(this.controls.length), title, control.element);
		tab.tabpanel.classList.add("flex_list", "-n1", "flex");
		this.tabset.add(tab);

		control.element.classList.add("flex_item");
		control.onRowVisibilityChange(() => {
			this.onRowVisibilityChange.trigger({ });
		});
		control.onRowsCreated(({ rows }) => {
			this.onRowsCreated.trigger({ rows });
		});
		if (this.controls.length === 0) {
			this.tabset.showTab("0", false);
		}
		if (this.database) {
			control.setDatabase(this.database);
		}
		this.controls.push(control);
	}

	private get activeModule(): SearchShuttleCandidateControl<D, R>|null {
		const activeTab = this.tabset.activeTab;
		if (!activeTab || !this.controls.hasOwnProperty(activeTab.tabId)) {
			return null;
		}
		return this.controls[Number(activeTab.tabId)];
	}

	getVisibleItems(pageSize: number): R[] {
		return this.activeModule?.getVisibleItems(pageSize) || [];
	}

	scrollIntoView(value: D): void {
		this.activeModule?.scrollIntoView(value);
	}

	clear(): void {
		this.controls.forEach(m => m.clear());
	}

	setHeight(height: number|null): void {
		this.controls.forEach(m => {
			m.setHeight(height ? ((height - this.element.offsetHeight) + m.element.offsetHeight) : null);
		});
	}

	setDatabase(database: SearchShuttleDb<D>): void {
		this.controls.forEach(c => c.setDatabase(database));
	}
}


export class SearchShuttleDbRecord<D extends number|string> {
	public onActiveChange = EventListener.create<{ record: SearchShuttleDbRecord<D> }>("activeChange");
	private activeValue: boolean;
	constructor(
		public readonly value: D,
		active: boolean,
		public readonly defaultActive: boolean
	) {
		this.activeValue = active;
	}

	set active(value: boolean) {
		if (value === this.activeValue) {
			return;
		}
		this.activeValue = value;
		this.onActiveChange.trigger({ record: this });
	}

	get active(): boolean {
		return this.activeValue;
	}
}

class SearchShuttleDb<D extends number|string> {
	public onUpdate   = EventListener.create<{ record: SearchShuttleDbRecord<D> }>("update");
	public onRegister = EventListener.create<{ records: Array<SearchShuttleDbRecord<D>> }>("register");
	private records: Record<string, SearchShuttleDbRecord<D>> = { };

	public registerMany(values: Array<{ value: D, active: boolean, defaultActive: boolean }>): Record<string, SearchShuttleDbRecord<D>> {
		const out: Record<string, SearchShuttleDbRecord<D>> = { };
		values.forEach(({ value, active, defaultActive }) => {
			const extantRecord = this.getEntry(value);
			if (extantRecord) {
				out[String(value)] = extantRecord;
				return;
			}
			const record = new SearchShuttleDbRecord<D>(value, active, defaultActive);
			record.onActiveChange(() => {
				this.onUpdate.trigger({ record });
			});
			this.records[ String(value) ] = record;
			out[ String(value) ] = record;
		});
		this.onRegister.trigger({ records: Object.values(out) });
		return out;
	}

	public register(value: D, active: boolean, defaultActive: boolean): SearchShuttleDbRecord<D> {
		return this.registerMany([{ value, active, defaultActive }])[String(value)];
	}

	public getEntry(value: D): SearchShuttleDbRecord<D>|null {
		return this.records.hasOwnProperty(String(value)) ? this.records[String(value)] : null;
	}

	public setWasModified(): boolean {
		for (const key in this.records) {
			if (!this.records.hasOwnProperty(key)) {
				continue;
			}
			const { active, defaultActive } = this.records[key];
			if (active != defaultActive) {
				return true;
			}
		}
		return false;
	}

	public getSet(): D[] {
		const out: D[] = [];
		for (const key in this.records) {
			if (this.records.hasOwnProperty(key) && this.records[key].active) {
				out.push(this.records[key].value);
			}
		}
		return out;
	}

	public getDiff(): { defaultState: D[], added: D[], removed: D[], state: D[] } {
		const out: { defaultState: D[], added: D[], removed: D[], state: D[] } = { defaultState: [], added: [], removed: [], state: [] };
		Object.values(this.records).forEach(r => {
			if (r.defaultActive) {
				out.defaultState.push(r.value);
			}
			if (r.active) {
				out.state.push(r.value);
			}
			if (r.active && !r.defaultActive) {
				out.added.push(r.value);
			}
			if (!r.active && r.defaultActive) {
				out.removed.push(r.value);
			}
		});
		return out;
	}
}

export interface SearchShuttleRow<D extends string|number> {
	element: HTMLElement;
	record: SearchShuttleDbRecord<D>;
}

export interface SearchShuttleViewData extends ViewData {
	includedHeader: string;
	candidateList: Node;
	searchHeader: string;
}

export interface SearchShuttleView extends View<SearchShuttleViewData> {
	selectedListContainer: HTMLDivElement;
}


const html = `
<div class="flex_item flex -row -gutter">
	<div class="flex_item -n:1 -of:2 flex -column -gutter" data-myon-name="selectedListContainer">
		<h2 class="flex_item g_ellipsis">{{ includedHeader }}</h2>
	</div>
	<div class="flex_item -n:1 -of:2 flex -column -gutter">
		<h3 class="flex_item">{{ searchHeader }}</h3>
		{{ candidateList @noescape }}
	</div>
</div>`;

export class SearchShuttle<D extends string|number, R extends SearchShuttleRow<D> = SearchShuttleRow<D>>{

	public readonly onRowVisibilityChange = EventListener.create<Event>("rowVisibilityChange");

	private view: SearchShuttleView;
	private controls: Record<string, R> = { };
	public readonly database: SearchShuttleDb<D> = new SearchShuttleDb<D>();
	private readonly selectedList: Listbox<D> = new Listbox<D>();
	protected candidateList?: SearchShuttleCandidateControl<D, R>;
	private handleVisibilityChange: EventHandler<any>;

	constructor(
		initialSet: D[],
		protected rowFactory: (record: SearchShuttleDbRecord<D>) => R,
		protected strings: {
			includedHeader: string,
			searchHeader: string,
			emptyMessage?: string
		}
	) {
		this.view = new View<SearchShuttleViewData>(html, { }, {
			...strings,
			candidateList: document.createTextNode("")
		}) as SearchShuttleView;
		this.selectedList.selectable = false;
		this.selectedList.element.classList.add("-overflow", "-flex", "flex_item", "-grow");
		if (strings.emptyMessage) {
			this.selectedList.showMessage(strings.emptyMessage);
		}
		this.handleVisibilityChange = debounce(() => {
			this.onRowVisibilityChange.trigger({ });
		});
		this.selectedList.onScroll(this.handleVisibilityChange);
		this.selectedList.onModified(this.handleVisibilityChange);
		this.view.selectedListContainer.appendChild(this.selectedList.element);

		const handleRecordUpdate = debounce((record: SearchShuttleDbRecord<D>) => {
			this.scrollToItem(record.value);
		});
		this.database.onUpdate(({ record }) => {
			if (record.active) {
				this.addToSet(record.value);
			} else {
				this.removeFromSet(record.value);
			}
			handleRecordUpdate(record);
		});
		this.database.onRegister(({ records }) => {
			this.addToSet(...records.filter(({ defaultActive }) => defaultActive).map(({ value }) => value));
		});

		this.database.registerMany(initialSet.map(value => ({ value, active: true, defaultActive: true })));
		this.onRowVisibilityChange.trigger({ });
	}

	public setCandidateList(candidateList: SearchShuttleCandidateControl<D, R>): void {
		if (this.candidateList) {
			this.candidateList.onRowVisibilityChange.unsubscribe(this.handleVisibilityChange);
		}
		this.view.set({ candidateList: candidateList.element });
		this.candidateList = candidateList;
		this.candidateList.setDatabase(this.database);
		this.candidateList.element.classList.add("flex_item", "-grow");
		this.candidateList.onRowVisibilityChange(this.handleVisibilityChange);
	}

	public addToSet(...values: D[]): void {
		if (values.length === 0) {
			return;
		}
		const adds: Array<{ id: D, node: Node }> = [];
		this.selectedList.hideMessage();
		values.forEach((value) => {
			const entry = this.database.getEntry(value);
			if (!entry) {
				throw new Error("No matching entry");
			}
			if (!this.controls.hasOwnProperty(value)) {
				this.controls[String(value)] = this.rowFactory(entry);
			}
			entry.active = true;
			if (!this.selectedList.contains(value)) {
				adds.push({ id: value, node: this.controls[String(value)].element });
			}
		});
		this.selectedList.addMany(adds);
	}

	public scrollToItem(value: D): void {
		this.selectedList.scrollIntoView(value);
		if (this.candidateList) {
			this.candidateList.scrollIntoView(value);
		}
	}

	public removeFromSet(value: D): void {
		const entry = this.database.getEntry(value);
		if (!entry) {
			return;
		}
		entry.active = false;
		if (!entry.defaultActive && this.selectedList.contains(value)) {
			this.selectedList.remove(value);
		}
		if (this.selectedList.isEmpty() && this.strings.emptyMessage) {
			this.selectedList.showMessage(this.strings.emptyMessage);
		}
	}

	public clear(): void {
		if (this.candidateList) {
			this.candidateList.clear();
		}
	}

	public setHeight(height: number|null): void {
		this.element.style.height = height ? `${height}px` : "";
	}

	public get element(): HTMLElement {
		return this.view.element;
	}

	public getVisibleItems(pageSize: number): R[] {
		const items = this.selectedList.getVisibleOptions(pageSize).map(({ id }) => this.controls[String(id)]);
		if (this.candidateList) {
			return [...items, ...this.candidateList.getVisibleItems(pageSize)];
		}
		return items;
	}
}
